import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuth } from '../context/AuthContext';

const MFAVerify: React.FC = () => {
  const navigate = useNavigate();
  const { pendingEmail, login } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!pendingEmail) {
    navigate('/login');
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.verifyMFA({
        email: pendingEmail,
        code,
      });
      login(response);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Código MFA inválido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        .mfa-input::placeholder {
          color: rgba(255, 255, 255, 0.25) !important;
        }
        .mfa-input:focus {
          border-color: var(--accent) !important;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15) !important;
          background: rgba(255, 255, 255, 0.08) !important;
        }
        .mfa-input:hover {
          border-color: rgba(255, 255, 255, 0.3) !important;
          background: rgba(255, 255, 255, 0.06) !important;
        }
        .mfa-btn:hover {
          filter: brightness(1.1);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
        }
        .mfa-btn:active {
          transform: translateY(0);
        }
        .back-btn:hover {
          color: #ffffff !important;
          text-decoration: underline;
        }
      `}</style>
      <div style={styles.card}>
        <div style={styles.brand}>
          <div style={styles.brandText}>Strategic Intelligence</div>
          <div style={styles.brandSub}>Verificación de seguridad</div>
        </div>

        <h1 style={styles.title}>Verificación MFA</h1>
        <p style={styles.description}>
          Ingresa el código de 6 dígitos de tu aplicación autenticadora.
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Código MFA</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              maxLength={6}
              className="mfa-input"
              style={styles.input}
              placeholder="000000"
              autoFocus
            />
          </div>

          <button type="submit" disabled={loading} className="mfa-btn" style={styles.button}>
            {loading ? 'Verificando...' : 'Verificar'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="back-btn"
          style={styles.backButton}
        >
          ← Volver al login
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-page)',
    fontFamily: 'var(--font-body)',
    padding: '1rem',
  },
  card: {
    background: 'var(--bg-sidebar)',
    padding: '2.5rem',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-lg)',
    width: '100%',
    maxWidth: '420px',
    textAlign: 'center',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  brand: {
    marginBottom: '1.5rem',
  },
  brandText: {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#ffffff',
  },
  brandSub: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--text-sidebar)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginTop: '0.25rem',
    opacity: 0.7,
  },
  title: {
    color: '#ffffff',
    marginBottom: '0.5rem',
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  description: {
    color: 'var(--text-sidebar)',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
    opacity: 0.8,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.125rem',
    textAlign: 'left',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--text-sidebar)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  input: {
    padding: '0.85rem 1rem',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
    fontSize: '1.25rem',
    textAlign: 'center',
    letterSpacing: '0.5rem',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
    fontFamily: 'var(--font-body)',
  },
  button: {
    padding: '0.9rem',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
    transition: 'background 0.2s, transform 0.1s, box-shadow 0.2s',
    fontFamily: 'var(--font-body)',
  },
  error: {
    background: 'rgba(220, 38, 38, 0.12)',
    color: '#f87171',
    padding: '0.75rem 1rem',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    marginBottom: '1rem',
    textAlign: 'left',
    border: '1px solid rgba(220, 38, 38, 0.25)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: 'var(--text-sidebar)',
    marginTop: '1.5rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontFamily: 'var(--font-body)',
    transition: 'color 0.2s',
    opacity: 0.8,
  },
};

export default MFAVerify;
